// src/layout/hooks/useScrollRestoration.ts
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollPosition {
    [key: string]: number;
}

export function useScrollRestoration(key?: string) {
    const location = useLocation();
    // @ts-ignore
    const positions = useRef<ScrollPosition>(() => {
        // Try to load from sessionStorage on initial load
        try {
            const saved = sessionStorage.getItem('scrollPositions');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    const scrollTimeout = useRef<NodeJS.Timeout>();
    const isRestoring = useRef(false);

    const saveScrollPosition = useCallback((path: string, position: number = window.scrollY) => {
        if (!isRestoring.current) { // Don't save while we're restoring
            positions.current[path] = position;
            // Persist to sessionStorage to survive page refresh
            try {
                sessionStorage.setItem('scrollPositions', JSON.stringify(positions.current));
            } catch (error) {
                console.error('Failed to save scroll position:', error);
            }
        }
    }, []);

    const restoreScrollPosition = useCallback((path: string) => {
        const savedPosition = positions.current[path];
        isRestoring.current = true;

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            if (savedPosition !== undefined) {
                window.scrollTo({
                    top: savedPosition,
                    behavior: 'auto' // Use 'smooth' if you want smooth scrolling
                });
            } else {
                window.scrollTo({
                    top: 0,
                    behavior: 'auto'
                });
            }

            // Reset restoring flag after scroll completes
            setTimeout(() => {
                isRestoring.current = false;
            }, 100);
        });
    }, []);

    // Save scroll position on scroll with debounce
    useEffect(() => {
        const path = key || location.pathname;

        const handleScroll = () => {
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }

            scrollTimeout.current = setTimeout(() => {
                saveScrollPosition(path, window.scrollY);
            }, 100);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
        };
    }, [location.pathname, key, saveScrollPosition]);

    // Restore scroll position when path changes
    useEffect(() => {
        const path = key || location.pathname;
        restoreScrollPosition(path);
    }, [location.pathname, key, restoreScrollPosition]);

    // Save position before unload
    useEffect(() => {
        const path = key || location.pathname;

        const handleBeforeUnload = () => {
            saveScrollPosition(path, window.scrollY);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [location.pathname, key, saveScrollPosition]);

    return {
        saveScrollPosition,
        restoreScrollPosition,
        getCurrentPosition: () => window.scrollY,
        clearPosition: (path?: string) => {
            if (path) {
                delete positions.current[path];
            } else {
                positions.current = {};
            }
            try {
                sessionStorage.setItem('scrollPositions', JSON.stringify(positions.current));
            } catch (error) {
                console.error('Failed to clear scroll positions:', error);
            }
        }
    };
}